/* eslint-disable consistent-return */
import { useState, useEffect, useRef } from "react";
import { isBrowser } from "../libs/utils/dom";
import { omit, typedKeys } from "../libs/utils/object";

type useScriptParameters = Partial<
    Pick<HTMLScriptElement, "onload" | "async">
> & {
    src: string;
    checkForExisting: boolean;
};

const useScript = (parameters: useScriptParameters) => {
    const { src, checkForExisting } = parameters;
    const attributes = omit(parameters, ["src", "checkForExisting"]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const scriptRef = useRef<HTMLScriptElement>();

    useEffect(() => {
        if (!isBrowser) return;

        if (checkForExisting) {
            const existing = document.querySelectorAll(`script[src="${src}"]`);
            if (existing.length > 0) {
                setLoading(false);
                return;
            }
        }

        const script = document.createElement("script");
        scriptRef.current = script;
        script.setAttribute("src", src);

        typedKeys(attributes).forEach((key) => {
            if (script[key] === undefined) {
                script.setAttribute(key, String(attributes[key]));
            } else {
                script[key] = attributes[key] as any;
            }
        });

        const handleLoad = () => {
            setLoading(false);
        };
        const handleError = (err: any) => {
            setError(err);
        };

        script.addEventListener("load", handleLoad);
        script.addEventListener("error", handleError);

        document.body.appendChild(script);

        return () => {
            script.removeEventListener("load", handleLoad);
            script.removeEventListener("error", handleError);
        };
    }, [attributes, checkForExisting, src]);

    return [loading, error, scriptRef.current];
};
export default useScript;
